import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListClaimsInput } from "@nexxt/contracts/claims";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the claims (50-api/claims.md, ADR-225, ADR-228): one factory, the same keys for the loader, the
// screens and the gestures of the Customer Service.

const claims = api.claims.claims;

type ListPage = InferResponseType<typeof claims.$get, 200>;
export type ClaimSummary = ListPage["items"][number];
export type ClaimDetail = InferResponseType<(typeof claims)[":claimId"]["$get"], 200>;

/** The filters of the list input (`listClaimsInput`), without the paging the queries add. */
export type ClaimFilters = Pick<ListClaimsInput, "type" | "status" | "outcome" | "customer" | "sku" | "salesOrder">;

const PAGE_SIZE = 50;

const keys = {
  all: ["claims"] as const,
  /** Prefix of every cached list: a gesture puts the claim it gets back in each of them. */
  lists: ["claims", "list"] as const,
};

export const claimQueries = {
  ...keys,

  list: (filters: ClaimFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          claims.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The claims of a sales order: a few, one page. */
  ofSalesOrder: (salesOrderId: string) =>
    queryOptions({
      queryKey: [...keys.all, "of", salesOrderId],
      queryFn: async ({ signal }) =>
        (await unwrap(claims.$get({ query: { salesOrder: salesOrderId, pageSize: "100" } }, { init: { signal } })))
          .items,
    }),

  /**
   * The orders a replacement can be (B-75): the latest of the claim's customer, made in sales; the claimed one the
   * dialog leaves out, and the domain refuses it again.
   */
  replacementOrders: (customerOrgId: string) =>
    queryOptions({
      queryKey: [...keys.all, "replacement-orders", customerOrgId],
      queryFn: async ({ signal }) =>
        (
          await unwrap(
            api.sales["sales-orders"].$get(
              { query: { customer: customerOrgId, pageSize: "100" } },
              { init: { signal } },
            ),
          )
        ).items,
    }),

  /** A claim with its photos and why its ticket could not be opened. */
  detail: (claimId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", claimId],
      queryFn: ({ signal }) => unwrap(claims[":claimId"].$get({ param: { claimId } }, { init: { signal } })),
    }),
};

/** Where a photo or a file of a claim is downloaded, as an attachment, with `claim.read` (ADR-228). */
export const attachmentHref = (claimId: string, fileId: string): string =>
  claims[":claimId"].files[":fileId"].$url({ param: { claimId, fileId } }).href;
